/**
 * Phase 3 — ObjectiveSelector
 *
 * Pick the objective.type + the conditional fields each one needs.
 * Procurement-register copy throughout.
 */
import { useId } from "react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const OBJECTIVES = [
    {
        value: "maximise_pk",
        label: "Maximise probability of kill",
        helper: "Best Pk we can find inside the design budget.",
    },
    {
        value: "reach_target_pk",
        label: "Reach a target Pk",
        helper: "Cheapest configuration that hits Pk ≥ target.",
    },
    {
        value: "minimise_cost_for_target_pk",
        label: "Minimise design cost at a target Pk",
        helper: "Lowest design cost subject to Pk ≥ target.",
    },
    {
        value: "minimise_miss_distance",
        label: "Minimise mean miss distance",
        helper: "Tightest geometric outcome regardless of Pk.",
    },
    {
        value: "maximise_efficiency",
        label: "Maximise design efficiency",
        helper: "Best Pk gain per unit of design cost.",
    },
    {
        value: "maximise_robustness",
        label: "Maximise robustness",
        helper: "Pk under environmental perturbations. Same as Pk in v1.",
    },
];

export default function ObjectiveSelector({ value, onChange }) {
    const id = useId();
    const obj = value || { type: "maximise_pk" };
    const needsTarget =
        obj.type === "reach_target_pk" ||
        obj.type === "minimise_cost_for_target_pk";
    const def = OBJECTIVES.find((o) => o.value === obj.type) || OBJECTIVES[0];

    const patch = (next) => onChange({ ...obj, ...next });

    return (
        <div className="space-y-3" data-testid="objective-selector">
            <div className="space-y-1">
                <Label htmlFor={`${id}-type`}>Objective</Label>
                <Select value={obj.type} onValueChange={(v) => patch({ type: v })}>
                    <SelectTrigger
                        id={`${id}-type`}
                        data-testid="objective-type-trigger"
                        className="bg-ux-bg-1"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {OBJECTIVES.map((o) => (
                            <SelectItem
                                key={o.value}
                                value={o.value}
                                data-testid={`objective-option-${o.value}`}
                            >
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[11px] text-ux-text-3">{def.helper}</p>
            </div>

            {needsTarget && (
                <div className="space-y-1">
                    <Label htmlFor={`${id}-target-pk`}>Target Pk</Label>
                    <Input
                        id={`${id}-target-pk`}
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={obj.target_pk ?? ""}
                        onChange={(e) =>
                            patch({
                                target_pk: e.target.value === "" ? null : parseFloat(e.target.value),
                            })
                        }
                        placeholder="0.75"
                        className="bg-ux-bg-1 max-w-[160px] tabular-nums"
                        data-testid="objective-target-pk"
                    />
                    <p className="text-[11px] text-ux-text-3">
                        Number between 0 and 1. The recommended configuration must
                        meet or exceed this value.
                    </p>
                </div>
            )}

            <div className="space-y-1">
                <Label htmlFor={`${id}-min-pk`}>Minimum acceptable Pk (optional)</Label>
                <Input
                    id={`${id}-min-pk`}
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={obj.minimum_pk ?? ""}
                    onChange={(e) =>
                        patch({
                            minimum_pk:
                                e.target.value === "" ? null : parseFloat(e.target.value),
                        })
                    }
                    placeholder="leave blank for no floor"
                    className="bg-ux-bg-1 max-w-[200px] tabular-nums"
                    data-testid="objective-min-pk"
                />
                <p className="text-[11px] text-ux-text-3">
                    Candidates below this floor are dropped from the ranking even
                    if they would otherwise win on cost.
                </p>
            </div>

            {obj.type === "maximise_robustness" && (
                <div className="space-y-1">
                    <Label htmlFor={`${id}-rob-pct`}>
                        Robustness perturbation (%)
                    </Label>
                    <Input
                        id={`${id}-rob-pct`}
                        type="number"
                        min={0}
                        max={50}
                        step={1}
                        value={obj.robustness_perturbation_pct ?? 15}
                        onChange={(e) =>
                            patch({
                                robustness_perturbation_pct:
                                    e.target.value === ""
                                        ? null
                                        : parseFloat(e.target.value),
                            })
                        }
                        className="bg-ux-bg-1 max-w-[160px] tabular-nums"
                        data-testid="objective-robustness-pct"
                    />
                    <p className="text-[11px] text-ux-text-3">
                        Robustness mode perturbs winds / sensors / lethality models by
                        this amount and rank the worst-case Pk.
                    </p>
                </div>
            )}
        </div>
    );
}
